import { useEffect, useRef, useCallback } from 'react'
import { refreshAccessToken } from '@/lib/api'

/**
 * Decode JWT payload without a library (base64url → JSON).
 * Returns null on any parse error.
 */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(payload))
  } catch {
    return null
  }
}

/**
 * Proactively refresh the access token when it's about to expire.
 *
 * On every user interaction (click, keydown, scroll, touchstart) we check
 * whether the current access token will expire within REFRESH_THRESHOLD_MS.
 * If so, we silently call /auth/refresh via the shared refreshAccessToken()
 * — a single in-flight Promise is shared with the axios interceptor to
 * prevent concurrent refreshes racing on the same cookie (loser would get
 * 401 → forced logout).
 *
 * The check is throttled to at most once per THROTTLE_MS to avoid spamming.
 */
const REFRESH_THRESHOLD_MS = 2 * 60 * 1000 // refresh if <2 min left
const THROTTLE_MS = 30 * 1000              // check at most every 30s

export function useSessionKeepAlive() {
  const lastCheckRef = useRef(0)

  const checkAndRefresh = useCallback(async () => {
    const now = Date.now()

    // Throttle
    if (now - lastCheckRef.current < THROTTLE_MS) return
    lastCheckRef.current = now

    const token = localStorage.getItem('accessToken')
    if (!token) return

    const payload = decodeJwtPayload(token)
    if (!payload?.exp) return

    const expiresAt = payload.exp * 1000
    const timeLeft = expiresAt - now

    // Token still has plenty of time
    if (timeLeft > REFRESH_THRESHOLD_MS) return

    try {
      await refreshAccessToken()
    } catch {
      // Refresh failed — don't force logout here, let the normal
      // 401 interceptor handle it on the next actual API call
    }
  }, [])

  useEffect(() => {
    const events = ['click', 'keydown', 'scroll', 'touchstart'] as const

    for (const event of events) {
      document.addEventListener(event, checkAndRefresh, { passive: true })
    }

    return () => {
      for (const event of events) {
        document.removeEventListener(event, checkAndRefresh)
      }
    }
  }, [checkAndRefresh])
}
